import { z } from 'zod';
import { CALL_FOLLOW_UP_KINDS, type CallFollowUpKind } from './constants';
import type { FollowUpContent, FollowUpTriggerType } from './decide';

/**
 * What a call follow-up task stores in `tasks.structured_data`.
 *
 * `type: 'call_follow_up'` is the discriminator every reader switches on (the
 * assignment rules' inbound-team step, the task page). `kind` says which
 * follow-up; `content` is what the decision carried, for the page to show
 * without re-deriving it. `direction` and `interactionId` come from the call's
 * row, never from a producer.
 *
 * Written only by the tasks domain's follow-up service.
 */
export const CALL_FOLLOW_UP_TASK_TYPE = 'call_follow_up';

/** Every trigger, held exhaustive by the record: a new trigger type fails to compile here. */
const TRIGGER_TYPES: Readonly<Record<FollowUpTriggerType, true>> = {
  call_ended: true,
  call_lost: true,
  evaluated: true,
  campaign_ended: true,
  callback_lapsed: true,
  booking_refused: true,
  legacy_task: true,
};
export const FOLLOW_UP_TRIGGER_TYPES = Object.keys(TRIGGER_TYPES) as [
  FollowUpTriggerType,
  ...FollowUpTriggerType[],
];

export interface CallFollowUpTaskData {
  type: typeof CALL_FOLLOW_UP_TASK_TYPE;
  schemaVersion: 1;
  kind: CallFollowUpKind;
  trigger: FollowUpTriggerType;
  /** The call it came from; null for a campaign ending with no call. */
  interactionId: string | null;
  direction: 'inbound' | 'outbound' | null;
  /** The campaign run, for campaign follow-ups. */
  runId: string | null;
  content: FollowUpContent;
  /** An older voice worker's own `structuredData`, kept as it sent it. */
  legacy?: Record<string, unknown>;
  /**
   * The charts on an UNVERIFIED inbound caller's line when the task was filed
   * (ADR-358): the ids the text names, and how many there were in all. A hint
   * for staff, never the task's patient.
   */
  unverifiedLineCharts?: { ids: string[]; total: number };
}

/**
 * The envelope, checked. `content` is not re-validated here: it was produced
 * by `decideCallFollowUps` from validated facts, and a reader that needs one
 * kind's fields narrows on `kind`.
 */
export const callFollowUpTaskDataSchema = z.object({
  type: z.literal(CALL_FOLLOW_UP_TASK_TYPE),
  schemaVersion: z.literal(1),
  kind: z.enum(CALL_FOLLOW_UP_KINDS),
  trigger: z.enum(FOLLOW_UP_TRIGGER_TYPES),
  interactionId: z.string().uuid().nullable(),
  direction: z.enum(['inbound', 'outbound']).nullable(),
  runId: z.string().uuid().nullable(),
  content: z.record(z.string(), z.unknown()),
  legacy: z.record(z.string(), z.unknown()).optional(),
  unverifiedLineCharts: z
    .object({ ids: z.array(z.string().uuid()), total: z.number().int().nonnegative() })
    .optional(),
});

/** Whether a task's `structured_data` is a call follow-up's, and from an inbound call. */
export function isInboundCallFollowUp(structuredData: unknown): boolean {
  const parsed = callFollowUpTaskDataSchema.safeParse(structuredData);
  return parsed.success && parsed.data.direction === 'inbound';
}
